import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { endOfDay } from "date-fns";
import { deepFreeze, timestampToDate } from "../util/misc";

export const TASK_FIELDS = {
  title: "",
  assignedTo: null,
  assignedBy: null,
  dueDate: null,
  // Without a time, a task is due by the end of its due date's day, and
  // dueDate holds that day's midnight.
  hasDueTime: false,
  notes: "",
  status: "open", // open | complete | skipped | declined
  completedAt: null,
  completedBy: null,
  skippedAt: null,
  skippedBy: null,
  declinedAt: null,
  declinedBy: null,
  declineReason: "",
  sourceMessageID: null,
  sourceMessageText: "",
  // Who sent that message (a user ID)
  sourceMessageBy: null,
  // A task made from a notification: what it said, and where it pointed
  sourceNotificationID: null,
  sourceNotificationText: "",
  sourceUrl: "",
  // A task made from a project email (Correspondence): its subject, who it
  // is from, and the start of its text, kept for when it is gone
  sourceEmailID: null,
  sourceEmailSubject: "",
  sourceEmailFrom: "",
  sourceEmailText: "",

  // Options. Tell the creator when someone else closes it (completes or
  // skips it; a decline always tells them), and remind the assignee when
  // it is due.
  notifyCreatorOnComplete: true,
  notifyAssigneeWhenDue: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(TASK_FIELDS);

// What the status bar shows for a task. Labels are i18n keys in sentence
// case; `color` is a COLORS key (bind its bg / fg, never the key itself).
// Open is an open issue's colour; TaskView's action buttons take the colour
// of the status they lead to.
export const TASK_STATUS_TYPES = {
  open: { label: "Open", icon: "task", color: "warning" },
  overdue: { label: "Overdue", icon: "due_date", color: "negative" },
  complete: { label: "Completed", icon: "task_done", color: "positive" },
  skipped: { label: "Skipped", icon: "task_done", color: "info" },
  declined: { label: "Declined", icon: "decline", color: "negative" },
};
deepFreeze(TASK_STATUS_TYPES);

// Who closed a task, and when, by status.
const CLOSED_FIELDS = {
  complete: ["completedBy", "completedAt"],
  skipped: ["skippedBy", "skippedAt"],
  declined: ["declinedBy", "declinedAt"],
};

export default class Task extends DataStructure {
  constructor(projectID, id, data = {}) {
    data = sanitize(data, TASK_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.completedAt = timestampToDate(data.completedAt);
    data.skippedAt = timestampToDate(data.skippedAt);
    data.declinedAt = timestampToDate(data.declinedAt);
    data.dueDate = timestampToDate(data.dueDate);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy.bind(this),
      delete: this.canBeDeletedBy.bind(this),
      complete: this.canBeCompletedBy.bind(this),
    });
  }

  // Permissions

  canBeEditedBy(user) {
    return user.isEmployee && (user.id === this.data.createdBy || user.isAdmin);
  }

  canBeDeletedBy(user) {
    return user.isEmployee && (user.id === this.data.createdBy || user.isAdmin);
  }

  canBeCompletedBy(user) {
    if (!user.isEmployee || this.data.status !== "open") return false;
    return (
      user.id === this.data.assignedTo ||
      user.id === this.data.createdBy ||
      user.isAdmin
    );
  }

  // Skipping closes it undone: the creator's call. The assignee declines,
  // which hands it back.
  canBeSkippedBy(user) {
    return (
      user.isEmployee &&
      this.data.status === "open" &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  // Declining hands the task back to its creator, so only the assignee does it.
  canBeDeclinedBy(user) {
    return (
      user.isEmployee &&
      this.data.status === "open" &&
      user.id === this.data.assignedTo
    );
  }

  // Anyone the rules let update the task: its creator, assignee or an admin.
  canBeReopenedBy(user) {
    return (
      user.isEmployee &&
      this.data.status !== "open" &&
      (user.id === this.data.createdBy ||
        user.id === this.data.assignedTo ||
        user.isAdmin)
    );
  }

  // Database

  get path() {
    return Task.getPath(this.projectID, this.id);
  }

  static getPath(projectID, id) {
    let path = `projects/${projectID}/tasks`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  get stateID() {
    return this.projectID + this.id;
  }

  static getStateID(path) {
    const match = path.match(/^projects\/([^/]+)\/tasks\/([^/]+)$/);
    if (match) {
      return match[1] + match[2];
    }
    return path;
  }

  static toFirestore(task) {
    return task.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Task(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Computed

  get isOpen() {
    return this.data.status === "open";
  }

  get isComplete() {
    return this.data.status === "complete";
  }

  get isSkipped() {
    return this.data.status === "skipped";
  }

  get isDeclined() {
    return this.data.status === "declined";
  }

  // When the task falls overdue: its due time, or the end of its due day.
  get dueAt() {
    if (!this.data.dueDate) return null;
    return this.data.hasDueTime
      ? this.data.dueDate
      : endOfDay(this.data.dueDate);
  }

  get isOverdue() {
    return Boolean(
      this.data.status === "open" && this.dueAt && this.dueAt < new Date()
    );
  }

  get statusType() {
    if (this.isOverdue) {
      return TASK_STATUS_TYPES.overdue;
    }
    return TASK_STATUS_TYPES[this.data.status] || TASK_STATUS_TYPES.open;
  }

  // The user who completed, skipped or declined the task (null while open).
  get closedByUser() {
    const fields = CLOSED_FIELDS[this.data.status];
    const userID = fields ? this.data[fields[0]] : null;
    return userID ? store.state.main.users[userID] || null : null;
  }

  get closedAt() {
    const fields = CLOSED_FIELDS[this.data.status];
    return fields ? this.data[fields[1]] || null : null;
  }

  get assignedToUser() {
    return this.data.assignedTo
      ? store.state.main.users[this.data.assignedTo]
      : null;
  }

  get assignedByUser() {
    return this.data.assignedBy
      ? store.state.main.users[this.data.assignedBy]
      : null;
  }

  // Who sent the message it was made from. A task made before that was kept
  // asks the message itself, when the app has it.
  get sourceMessageByUser() {
    const message =
      this.data.sourceMessageID &&
      store.state.main.messages &&
      store.state.main.messages[this.data.sourceMessageID];
    const userID =
      this.data.sourceMessageBy || (message && message.data.createdBy);
    return userID ? store.state.main.users[userID] || null : null;
  }

  get createdByUser() {
    return this.createdBy;
  }

  // The dialog header's info (MetaInfo) prints who made and last changed
  // it: the users, not their IDs.
  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  get completedByUser() {
    return this.data.completedBy
      ? store.state.main.users[this.data.completedBy]
      : null;
  }

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }
}
